import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  AlertTriangle,
  FileText,
  Eye,
  EyeOff,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import websiteApi from '../../services/websiteApi';
import './BlogsManager.css';

interface BlogData {
  _id?: string;
  title: string;
  slug?: string;
  excerpt: string;
  content: string;
  author: string;
  category: string;
  tags: string[];
  featuredImage: string;
  isPublished: boolean;
  publishedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

const categories = [
  { value: 'all', label: 'All categories' },
  { value: 'general', label: 'General' },
  { value: 'news', label: 'News' },
  { value: 'tutorials', label: 'Tutorials' },
  { value: 'updates', label: 'Updates' },
  { value: 'announcements', label: 'Announcements' },
  { value: 'guides', label: 'Guides' },
];

const BlogsManager: React.FC = () => {
  const [blogs, setBlogs] = useState<BlogData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState<BlogData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [formData, setFormData] = useState<BlogData>({
    title: '',
    excerpt: '',
    content: '',
    author: 'Admin',
    category: 'general',
    tags: [],
    featuredImage: '',
    isPublished: false,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [tagsInput, setTagsInput] = useState('');

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, unknown> = { page, limit: 12 };
      if (searchTerm) params.search = searchTerm;
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (selectedStatus !== 'all') params.is_published = selectedStatus === 'published';

      const response = await websiteApi.getBlogs(params);
      setBlogs(response.blogs || []);
      setTotalPages(response.totalPages || 1);
      setTotalCount(response.totalCount || 0);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, selectedCategory, selectedStatus]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const handleSave = async () => {
    try {
      const blogPayload = {
        ...formData,
        tags: tagsInput.split(',').map((t) => t.trim()).filter(Boolean),
      };

      if (editingBlog?._id) {
        await websiteApi.updateBlog(editingBlog._id, blogPayload);
        setSuccessMessage('Blog updated');
      } else {
        await websiteApi.createBlog(blogPayload);
        setSuccessMessage('Blog created');
      }
      await fetchBlogs();
      setShowModal(false);
      setEditingBlog(null);
      resetForm();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save blog');
    }
  };

  const handleDelete = async (blogId: string) => {
    try {
      await websiteApi.deleteBlog(blogId);
      setSuccessMessage('Blog deleted');
      await fetchBlogs();
      setShowDeleteConfirm(null);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete blog');
    }
  };

  const handleTogglePublish = async (blog: BlogData) => {
    try {
      await websiteApi.toggleBlogPublish(blog._id!, !blog.isPublished);
      setSuccessMessage(blog.isPublished ? 'Blog unpublished' : 'Blog published');
      await fetchBlogs();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle publish status');
    }
  };

  const openEditModal = (blog?: BlogData) => {
    if (blog) {
      setEditingBlog(blog);
      setFormData(blog);
      setTagsInput(blog.tags?.join(', ') || '');
    } else {
      setEditingBlog(null);
      resetForm();
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      author: 'Admin',
      category: 'general',
      tags: [],
      featuredImage: '',
      isPublished: false,
    });
    setTagsInput('');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading && blogs.length === 0) {
    return (
      <div className="bm-page bm-loading">
        <RefreshCw size={20} className="bm-spin" />
        Loading blogs…
      </div>
    );
  }

  return (
    <div className="bm-page">
      <div className="bm-toolbar">
        <input
          type="text"
          className="bm-search"
          placeholder="Search title, author…"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
        />
        <select className="bm-select" value={selectedCategory} onChange={(e) => { setSelectedCategory(e.target.value); setPage(1); }}>
          {categories.map((cat) => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        <select className="bm-select" value={selectedStatus} onChange={(e) => { setSelectedStatus(e.target.value); setPage(1); }}>
          <option value="all">All status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
        <span className="bm-toolbar-note">{totalCount} posts</span>
        <div className="bm-toolbar-actions">
          <button type="button" className="bm-btn" onClick={fetchBlogs}>
            <RefreshCw size={14} className={loading ? 'bm-spin' : ''} />
          </button>
          <button type="button" className="bm-btn bm-btn--primary" onClick={() => openEditModal()}>
            <Plus size={14} /> New blog
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="bm-toast bm-toast--ok">
          <span>{successMessage}</span>
          <button type="button" onClick={() => setSuccessMessage('')} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}
      {error && (
        <div className="bm-toast bm-toast--err">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}

      <div className="bm-table-panel">
        {blogs.length === 0 ? (
          <div className="bm-empty">
            <FileText size={28} strokeWidth={1.5} />
            <p>{searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all' ? 'No matching blogs' : 'No blog posts yet'}</p>
            <button type="button" className="bm-btn bm-btn--primary" onClick={() => openEditModal()}>
              <Plus size={14} /> Create blog
            </button>
          </div>
        ) : (
          <>
            <div className="bm-table-scroll">
              <table className="bm-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Author</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map((blog) => (
                    <tr key={blog._id}>
                      <td>
                        <span className="bm-title">{blog.title}</span>
                        <span className="bm-excerpt">{blog.excerpt || 'No excerpt'}</span>
                      </td>
                      <td><span className="bm-meta">{blog.author || 'Admin'}</span></td>
                      <td><span className="bm-badge bm-badge--cat">{blog.category || 'general'}</span></td>
                      <td>
                        <span className={`bm-badge ${blog.isPublished ? 'bm-badge--published' : 'bm-badge--draft'}`}>
                          {blog.isPublished ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td><span className="bm-meta">{formatDate(blog.createdAt)}</span></td>
                      <td>
                        <div className="bm-actions">
                          <button type="button" className="bm-icon-btn" title={blog.isPublished ? 'Unpublish' : 'Publish'} onClick={() => handleTogglePublish(blog)}>
                            {blog.isPublished ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                          <button type="button" className="bm-icon-btn" title="Edit" onClick={() => openEditModal(blog)}>
                            <Edit size={14} />
                          </button>
                          <button type="button" className="bm-icon-btn bm-icon-btn--danger" title="Delete" onClick={() => setShowDeleteConfirm(blog._id!)}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="bm-pagination">
                <span>Page {page} of {totalPages}</span>
                <div className="bm-pagination-btns">
                  <button type="button" className="bm-btn" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    <ChevronLeft size={14} /> Prev
                  </button>
                  <button type="button" className="bm-btn" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <div className="bm-overlay" onClick={() => setShowModal(false)}>
          <div className="bm-modal bm-modal--lg" onClick={(e) => e.stopPropagation()}>
            <div className="bm-modal-header">
              <h3 className="bm-modal-title">{editingBlog ? 'Edit blog' : 'New blog'}</h3>
              <button type="button" className="bm-modal-close" onClick={() => setShowModal(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="bm-modal-body">
              <div className="bm-field">
                <label htmlFor="bm-title">Title</label>
                <input id="bm-title" type="text" value={formData.title} onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))} />
              </div>
              <div className="bm-form-row" style={{ marginTop: 12 }}>
                <div className="bm-field">
                  <label htmlFor="bm-author">Author</label>
                  <input id="bm-author" type="text" value={formData.author} onChange={(e) => setFormData((prev) => ({ ...prev, author: e.target.value }))} />
                </div>
                <div className="bm-field">
                  <label htmlFor="bm-category">Category</label>
                  <select id="bm-category" value={formData.category} onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}>
                    {categories.filter((c) => c.value !== 'all').map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="bm-field" style={{ marginTop: 12 }}>
                <label htmlFor="bm-excerpt">Excerpt</label>
                <textarea id="bm-excerpt" rows={2} value={formData.excerpt} onChange={(e) => setFormData((prev) => ({ ...prev, excerpt: e.target.value }))} />
              </div>
              <div className="bm-field">
                <label htmlFor="bm-content">Content</label>
                <textarea id="bm-content" rows={6} value={formData.content} onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))} />
              </div>
              <div className="bm-form-row">
                <div className="bm-field">
                  <label htmlFor="bm-tags">Tags (comma-separated)</label>
                  <input id="bm-tags" type="text" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} />
                </div>
                <div className="bm-field">
                  <label htmlFor="bm-image">Featured image URL</label>
                  <input id="bm-image" type="text" value={formData.featuredImage} onChange={(e) => setFormData((prev) => ({ ...prev, featuredImage: e.target.value }))} />
                </div>
              </div>
              <label className="bm-toggle-row" style={{ marginTop: 12 }}>
                <input type="checkbox" checked={formData.isPublished} onChange={(e) => setFormData((prev) => ({ ...prev, isPublished: e.target.checked }))} />
                Publish immediately
              </label>
            </div>
            <div className="bm-modal-footer">
              <button type="button" className="bm-btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="button" className="bm-btn bm-btn--primary" onClick={handleSave} disabled={!formData.title.trim()}>
                <Save size={14} /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="bm-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="bm-modal bm-modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="bm-modal-body">
              <div className="bm-confirm-icon"><AlertTriangle size={22} /></div>
              <p className="bm-confirm-text">Delete this blog post? This cannot be undone.</p>
            </div>
            <div className="bm-modal-footer">
              <button type="button" className="bm-btn" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
              <button type="button" className="bm-btn bm-btn--danger" onClick={() => handleDelete(showDeleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogsManager;
